import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Users,
  ChevronRight,
  Landmark,
  Loader2,
  User,
  Filter,
  ArrowUpDown, // Icon sort mặc định
  ArrowUp, // Icon tăng dần
  ArrowDown, // Icon giảm dần
} from "lucide-react";
import { customerService } from '../../../../services/apiService';
import getBankInfo from '../../../../services/service';
import { formatCurrency, formatDateTime } from '../../../../utils/format';

const CustomerList = ({ onSelect, isParentLoaded }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState("");

  // State phân trang
  const [pagination, setPagination] = useState({ limit: 50, cursor_amount: null, cursor_transactions: null, cursor_account: null });
  //const [pageInput, setPageInput] = useState(1);
  const [moreData, setMoreData] = useState(false);
  const [nextCursorAccount, setNextCursorAccount] = useState(null);
  const [nextCursorAmount, setNextCursorAmount] = useState(null);
  const [nextCursorTransactions, setNextCursorTransactions] = useState(null);

  // --- STATE FILTER & SORT ---
  const [onlyMB, setOnlyMB] = useState(false);

  // sortConfig: key là tên trường cần sort ('amount' hoặc 'tx_count')
  // direction: 'asc' hoặc 'desc'
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });

  //const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  // --- API FETCHING ---
  const fetchCustomers = useCallback(async () => {
    // Only fetch customers when term is null by default or when term has at least 4 characters
    if (term.length < 4 && term.length !== 0)
        return;
    setLoading(true);
    try {
      const params = {
        limit: pagination.limit,
        keyword: term || undefined,
        sort_by: sortConfig.key || undefined,
        sort_order: sortConfig.key ? sortConfig.direction : undefined,
        only_mb_bank: onlyMB,
        cursor_amount: pagination.cursor_amount,
        cursor_transactions: pagination.cursor_transactions,
        cursor_account: pagination.cursor_account
      };
      
      // Gọi qua service
      const response = await customerService.getReport(params);

      const data = response.data && response.data.data ? response.data.data : [];
      const hasMoreData = response.data.has_more || false;
      const nextCursorAccount = response.data.next_cursor_account || null;
      const nextCursorAmount = response.data.next_cursor_amount || null;
      const nextCursorTransactions = response.data.next_cursor_transactions || null;
      
      setMoreData(hasMoreData);
      setNextCursorAccount(nextCursorAccount);
      setNextCursorAmount(nextCursorAmount);
      setNextCursorTransactions(nextCursorTransactions);

      setCustomers(data);
    } catch (error) {
      console.error("Lỗi tải danh sách:", error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [pagination, term, sortConfig, onlyMB]);

  useEffect(() => {
    if (!isParentLoaded) return;
    const timeoutId = setTimeout(() => fetchCustomers(), 100);
    return () => clearTimeout(timeoutId);
  }, [fetchCustomers, isParentLoaded]);
  
  // useEffect(() => {
  //   setPageInput(currentPage);
  // }, [currentPage]);

  // --- HANDLER SORT (Giữ trang, để Smart Reset lo phần lỗi) ---
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key: key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key: key, direction: "desc" };
    });
  };

  const toggleMBFilter = () => {
    setOnlyMB((prev) => !prev);
    // setPagination((prev) => ({ ...prev, offset: 0 }));
    setPagination((prev) => ({ ...prev, cursor_account: null, cursor_amount: null, cursor_transactions: null }));
    
  };

  const handleNextPage = () => {
    if (moreData) {
      setPagination((prev) => ({ ...prev, cursor_account: nextCursorAccount, cursor_amount: nextCursorAmount, cursor_transactions: nextCursorTransactions }));
    }
  };

  // const handlePrevPage = () => {
  //   if (pagination.offset === 0) return;
  //   setPagination((prev) => ({
  //     ...prev,
  //     offset: Math.max(0, prev.offset - prev.limit),
  //   }));
  // };

  const handleLimitChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      limit: parseInt(e.target.value, 10),
      cursor_account: null, 
      cursor_amount: null, 
      cursor_transactions: null
    }));
  };

  // const handleGotoPage = (e) => {
  //   e.preventDefault();
  //   const pageNumber = parseInt(pageInput, 10);
  //   if (!isNaN(pageNumber) && pageNumber >= 1) {
  //     setPagination((prev) => ({
  //       ...prev,
  //       offset: (pageNumber - 1) * prev.limit,
  //     }));
  //   } else {
  //     setPageInput(currentPage);
  //   }
  // };

  // --- HELPER RENDER ICON SORT ---
  const renderSortIcon = (columnKey) => {
    // Nếu không sort cột này -> Hiện icon mờ
    if (sortConfig.key !== columnKey) {
      return (
        <ArrowUpDown
          size={14}
          className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
        />
      );
    }
    // Nếu đang sort -> Hiện icon xanh đậm tương ứng chiều
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={14} className="text-blue-600 font-bold" />
    ) : (
      <ArrowDown size={14} className="text-blue-600 font-bold" />
    );
  };

  // Helper đổi màu background header khi đang sort
  const getHeaderClass = (columnKey) => {
    return `px-6 py-3 cursor-pointer select-none transition-colors group ${
      sortConfig.key === columnKey ? "bg-blue-50/50" : "hover:bg-slate-50"
    }`;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        {/* --- TOOLBAR --- */}
        <div className="flex flex-col xl:flex-row gap-4 justify-between items-start xl:items-center mb-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2 shrink-0">
            <Users size={20} className="text-blue-600" /> Danh sách khách hàng
          </h2>

          <div className="flex flex-col sm:flex-row w-full xl:w-auto gap-3">
            <button
              onClick={toggleMBFilter}
              className={`flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                onlyMB
                  ? "bg-blue-50 border-blue-200 text-blue-700 shadow-sm"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Filter size={16} className={onlyMB ? "fill-blue-700" : ""} />
              {onlyMB ? "Đang lọc: Chỉ MB Bank" : "Lọc: Chỉ MB Bank"}
            </button>

            <div className="relative w-full sm:w-80">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                placeholder="Tìm Tên hoặc Số tài khoản(Tối thiểu 4 ký tự)"
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 transition-all"
                value={term}
                onChange={(e) => {
                  setTerm(e.target.value)
                }}
              />
            </div>
          </div>
        </div>

        {/* --- TABLE --- */}
        {loading ? (
          <div className="h-64 flex items-center justify-center text-slate-500 gap-2">
            <Loader2 className="animate-spin" /> Đang tải dữ liệu...
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-slate-100">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase font-semibold text-xs border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3">Khách hàng / Tài khoản</th>
                    <th className="px-6 py-3">Ngân hàng</th>

                    {/* --- CỘT SORT: TỔNG CHI TIÊU (Đã thêm min-w-[180px]) --- */}
                    <th
                      // Thêm class min-w-[180px] vào đây để cột rộng hơn
                      className={`${getHeaderClass("amount")} min-w-[180px]`}
                      onClick={() => handleSort("amount")}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        Tổng chi tiêu
                        {renderSortIcon("amount")}
                      </div>
                    </th>

                    {/* --- CỘT SORT: SỐ GIAO DỊCH --- */}
                    <th
                      className={getHeaderClass("tx_count")}
                      onClick={() => handleSort("transactions")}
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        Số GD
                        {renderSortIcon("tx_count")}
                      </div>
                    </th>

                    <th className="px-6 py-3 text-right">GD Cuối</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {customers.map((cust, idx) => {
                    const bankInfo = getBankInfo(cust.bank_code);
                    return (
                      <tr
                        key={idx}
                        onClick={() => onSelect(cust)}
                        className="hover:bg-blue-50 transition-colors cursor-pointer group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold border border-slate-200 flex-shrink-0 shadow-sm">
                              {cust.customerName ? (
                                cust.customerName.charAt(0)
                              ) : (
                                <User size={16} />
                              )}
                            </div>
                            <div>
                              <p className="font-bold text-slate-900 group-hover:text-blue-600 line-clamp-1 max-w-[200px] transition-colors">
                                {cust.customerName || "Khách vãng lai"}
                              </p>
                              <p className="text-xs text-slate-500 font-mono">
                                {cust.customer_account}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium border border-transparent ${bankInfo.bg} ${bankInfo.color} whitespace-nowrap`}
                          >
                            {bankInfo.logo ? (
                              <img
                                src={bankInfo.logo}
                                alt={bankInfo.name}
                                className=" w-10 object-contain"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <Landmark size={14} />
                            )}
                            {bankInfo.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center font-bold text-slate-900 tabular-nums">
                          {formatCurrency(cust.totalAmount)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="bg-slate-100 px-2.5 py-1 rounded text-slate-700 font-medium tabular-nums">
                            {cust.number_transaction.toLocaleString("vi-VN")}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-slate-500 text-xs tabular-nums">
                          {formatDateTime(cust.last_tx_time)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ChevronRight
                            size={18}
                            className="text-slate-300 group-hover:text-blue-500 transition-colors"
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {customers.length === 0 && (
                    <tr>
                      <td
                        colSpan="6"
                        className="text-center py-12 text-slate-500 flex flex-col items-center gap-2"
                      >
                        <User size={32} className="text-slate-300" />
                        <span>
                          Không tìm thấy khách hàng nào phù hợp điều kiện lọc.
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* --- PAGINATION --- */}
            <div className="px-6 py-4 border-t border-slate-200 flex flex-col xl:flex-row items-center justify-between gap-4 bg-slate-50/50 rounded-b-xl">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                {/* <p className="text-sm text-slate-500 font-medium">
                  Trang {currentPage}
                </p> */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Hiển thị:</span>
                  <select
                    value={pagination.limit}
                    onChange={handleLimitChange}
                    className="border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500 bg-white text-slate-700 cursor-pointer h-8 shadow-sm"
                  >
                    {[5, 10, 20, 50, 100].map((val) => (
                      <option key={val} value={val}>
                        {val}
                      </option>
                    ))}
                  </select>
                </div>
                {/* <form
                  onSubmit={handleGotoPage}
                  className="flex items-center gap-2"
                >
                  <span className="text-sm text-slate-500">Đến trang:</span>
                  <input
                    type="number"
                    min="1"
                    className="w-16 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500 h-8 text-center shadow-sm"
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="px-3 py-1 bg-white border border-slate-300 rounded text-sm hover:bg-slate-50 text-slate-700 h-8 font-medium shadow-sm transition-colors"
                  >
                    Go
                  </button>
                </form> */}
              </div>

              <div className="flex gap-2">
                {/* <button
                  onClick={handlePrevPage}
                  disabled={pagination.offset === 0}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded text-sm text-slate-600 disabled:opacity-50 hover:bg-slate-50 font-medium shadow-sm transition-colors"
                >
                  Trước
                </button> */}
                <button
                  onClick={handleNextPage}
                  disabled={!moreData}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded text-sm text-slate-600 disabled:opacity-50 hover:bg-slate-50 font-medium shadow-sm transition-colors"
                >
                  Tiếp
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerList;
